import { mdiFlashOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { Button, Empty, Popover, Row, Select, SelectProps, Skeleton, Space, Typography, theme } from 'antd';
import Link from 'next/link';
import { CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Can from '../../../containers/Can';
import { subscriptionFeatureMap, useSubscription } from '../../../containers/subscription/SubscriptionController';
import useMacroList from '../../../hooks/macrosPage/useMacroList';
import { ticketInteractionViewViewType } from '../../../model/types';
import SubscriptionBadge from '../../Subscription/SubscriptionBadge';
import { handleOnSelect } from './utils';

const styles: Record<string, CSSProperties> = {
    selectWidth: { width: 300, position: 'relative' }
};

function EmptyMenu() {
    const { t } = useTranslation('tickets');
    return (
        <>
            <Empty imageStyle={{ height: '100px' }} description={t('macro.notAvailable')} />
            <Can see>
                {/* TODO Utku Make Button add a Tab instead of going into a new Tab */}
                <Button style={{ margin: '1rem 2rem' }}>
                    <Link href={'/admin/macros/new'}>
                        <a>{t('macro.create')}</a>
                    </Link>
                </Button>
            </Can>
        </>
    );
}

function Placeholder({ isOpen }: { isOpen: boolean }) {
    const { t } = useTranslation('tickets');

    if (isOpen) return <>{t('macro.searchFields')}</>;

    return (
        <Space>
            <Icon
                path={mdiFlashOutline}
                size="19px"
                rotate={20}
                style={{ color: 'rgba(0, 0, 0, 0.85)', marginBottom: '-5px' }}
            />

            <Typography.Text>{t('macro.apply')}</Typography.Text>
        </Space>
    );
}

interface MacroSelectProps {
    handleSelect?: (macroId: number) => void;
}

function MacroSelect({ handleSelect }: MacroSelectProps) {
    const {
        token: { zIndexPopupBase }
    } = theme.useToken();
    const { data: macroList, isLoading } = useMacroList(true);

    const [open, setIsOpen] = useState(false);
    const { subscriptionFeatureCheck } = useSubscription();
    const isMacroSupported = subscriptionFeatureCheck(subscriptionFeatureMap.SUPPORT_MACRO);

    if (isLoading) return <Skeleton.Input active style={styles.selectWidth} />;

    const options: SelectProps['options'] = macroList?.map((macro) => {
        const { id, description, name } = macro;
        return {
            value: name,
            key: id,
            label: (
                <Popover
                    placement="right"
                    title={name}
                    content={description}
                    trigger="hover"
                    arrowContent={null}
                    // https://github.com/ant-design/ant-design/blob/master/components/select/style/index.tsx#L342
                    overlayStyle={{ zIndex: zIndexPopupBase + 50 + 1 }}
                >
                    <Row className="full-width">{name}</Row>
                </Popover>
            )
        };
    });

    return (
        <SubscriptionBadge isShow={!isMacroSupported}>
            <Popover content={<EmptyMenu />} trigger={macroList?.length === 0 ? ['hover'] : []}>
                <Select
                    disabled={macroList?.length === 0}
                    onDropdownVisibleChange={setIsOpen}
                    onSelect={(_v, { key }) => handleSelect?.(key)}
                    optionFilterProp="value"
                    open={open}
                    // open
                    placeholder={<Placeholder isOpen={open} />}
                    showSearch
                    style={styles.selectWidth}
                    value={null}
                    options={options}
                    dropdownAlign={{ points: ['bc', 'tc'], offset: [0, -5] }}
                />
            </Popover>
        </SubscriptionBadge>
    );
}

export default function MacroButton({ viewType }: { viewType?: ticketInteractionViewViewType }) {
    const { t } = useTranslation('tickets');

    const { data: macroList } = useMacroList(true);

    const errorOnNoActions = t('macro.noActions');

    const handleMacroActionOnSelect = (macroId: number) => {
        const macro = macroList.find((m) => m.id === macroId);
        if (!macro) return;
        return handleOnSelect(macro, errorOnNoActions, viewType);
    };

    return (
        <Space wrap>
            <MacroSelect handleSelect={handleMacroActionOnSelect} />
        </Space>
    );
}
